import { useEffect, useState } from "react";
import withLoader from "../../components/layouts/WithLoader";
import Select from "react-select";
import { Alert, Snackbar } from "@mui/material";
import WarehouseService from "../../services/WarehouseService";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import DataTableBonDeSortieSell from "../../ui/DataTableBonDeSortieSell";

const labelStyle = {
  fontSize: "0.8rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#6c757d",
};

const dateToInput = (value) => (value === "any" ? "" : value.split("T")[0]);

// Shared page for the warehouse documents lists (bons de réception / de sortie).
// transactionType: 0 = réception, 1 = sortie
const ListBonDocuments = ({
  startLoading,
  stopLoading,
  transactionType,
  title,
  icon,
  subtitle,
  singular,
  plural,
}) => {
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [warehouseSourceId, setWarehouseSourceId] = useState(0);
  const [warehousesLoading, setWarehousesLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateBegin, setDateBegin] = useState("any");
  const [dateEnd, setDateEnd] = useState("any");

  useEffect(() => {
    WarehouseService.getAllRealWarehouse(getCurrentUserId(), 0)
      .then((response) => {
        if (response.data.success) {
          setWarehouseOptions(
            response.data.success
              .filter((element) => element && element.id !== undefined)
              .map((element) => ({ value: element.id, label: element.name }))
          );
        } else {
          setAlert({ severity: "error", text: "Erreur : pas de dépôt" });
        }
      })
      .catch(() =>
        setAlert({
          severity: "error",
          text: "Erreur lors du chargement des dépôts",
        })
      )
      .finally(() => setWarehousesLoading(false));
  }, []);

  const onGetDocumentsByFilter = () => {
    if (!warehouseSourceId) {
      setAlert({ severity: "error", text: "Veuillez choisir un dépôt" });
      return;
    }
    setLoading(true);
    startLoading();
    WarehouseService.getProductTransactionHistoryByWarehouseBetweenDates(
      warehouseSourceId,
      getCurrentUserId(),
      transactionType,
      dateBegin,
      dateEnd
    )
      .then((result) => {
        setTransactions(result.data.success || []);
        setSearched(true);
      })
      .catch(() => {
        setAlert({
          severity: "error",
          text: `Erreur lors du chargement : ${plural}`,
        });
      })
      .finally(() => {
        setLoading(false);
        stopLoading();
      });
  };

  const onResetFilters = () => {
    setWarehouseSourceId(0);
    setDateBegin("any");
    setDateEnd("any");
    setTransactions([]);
    setSearched(false);
  };

  const onChangeDateBegin = (event) => {
    const value = event.target.value;
    setDateBegin(value ? value + "T00:00:00" : "any");
  };

  const onChangeDateEnd = (event) => {
    const value = event.target.value;
    setDateEnd(value ? value + "T23:59:59" : "any");
  };

  const selectedWarehouse =
    warehouseOptions.find((option) => option.value === warehouseSourceId) ||
    null;

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <h4 className="mb-1">
            <i className={`fas ${icon} mr-2`}></i>
            {title}
          </h4>
          <small className="text-muted">
            {subtitle}
          </small>
        </div>
      </section>

      <div className="content">
        <div className="container-fluid">
          <div
            className="card card-outline card-dark"
            style={{ borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" }}
          >
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-filter mr-2"></i>Filtres
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label style={labelStyle}>Dépôt</label>
                    <Select
                      isClearable={false}
                      isLoading={warehousesLoading}
                      placeholder="Choisir un dépôt"
                      noOptionsMessage={() => "Aucun dépôt"}
                      loadingMessage={() => "Chargement..."}
                      options={warehouseOptions}
                      value={selectedWarehouse}
                      onChange={(option) =>
                        setWarehouseSourceId(option ? option.value : 0)
                      }
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="dateDebut" style={labelStyle}>
                      Date début
                    </label>
                    <input
                      id="dateDebut"
                      type="date"
                      className="form-control"
                      value={dateToInput(dateBegin)}
                      onChange={onChangeDateBegin}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="dateFin" style={labelStyle}>
                      Date fin
                    </label>
                    <input
                      id="dateFin"
                      type="date"
                      className="form-control"
                      value={dateToInput(dateEnd)}
                      onChange={onChangeDateEnd}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer text-right">
              <button
                className="btn btn-outline-secondary mr-2"
                onClick={onResetFilters}
                disabled={loading}
              >
                <i className="fas fa-undo mr-1"></i>
                Réinitialiser
              </button>
              <button
                className="btn btn-dark"
                onClick={onGetDocumentsByFilter}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Chargement...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search mr-1"></i>
                    Valider le choix
                  </>
                )}
              </button>
            </div>
          </div>

          {!searched && !loading && (
            <div className="text-center text-muted py-5">
              <i className="fas fa-clipboard-list fa-2x mb-2 d-block"></i>
              Choisissez un dépôt puis validez pour afficher les {plural}
            </div>
          )}

          {searched && transactions.length === 0 && (
            <div className="text-center text-muted py-5">
              <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
              Aucun {singular} pour cette sélection
            </div>
          )}

          {searched && transactions.length > 0 && (
            <>
              <div className="mb-2 text-muted">
                <span className="badge badge-dark mr-1">
                  {transactions.length}
                </span>
                {transactions.length > 1 ? plural : singular}
              </div>
              {transactions.map((element, index) => (
                <DataTableBonDeSortieSell
                  key={element?.id ?? index}
                  document={element}
                  listTransactions={element?.transactionOperations}
                />
              ))}
            </>
          )}
        </div>
      </div>

      {alert && (
        <Snackbar
          open
          autoHideDuration={3000}
          onClose={() => setAlert(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            variant="filled"
            onClose={() => setAlert(null)}
            severity={alert.severity}
          >
            {alert.text}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};
export default withLoader(ListBonDocuments);
